import type { ReactNode } from "react";
import { cn } from "./cn";

export interface TabDefinition<K extends string> {
  key: K;
  label: string;
  /** Shown after the label — a count, usually. Absent rather than zero when there is nothing. */
  badge?: ReactNode;
  /** Belongs to a phase that has not been built yet, so it is named rather than hidden. */
  phase?: string;
}

/**
 * The tab strip this application already used in three places, made into one component.
 *
 * <p>It was copied into the project page, the work-item activity feed and the reports page, and the
 * three had already drifted on padding and on how the active tab is drawn. Consolidating matters
 * more than the duplication itself: tabs are how somebody learns the shape of a page, and a strip
 * that looks slightly different on each screen quietly costs the reader that.
 *
 * <p>Unbuilt tabs are shown disabled with their phase named, the same way the sidebar treats
 * unbuilt sections. Hiding them makes the product look smaller than it is and leaves people
 * wondering whether a feature exists; naming the phase sets the expectation honestly.
 */
export function Tabs<K extends string>({
  tabs,
  active,
  onChange,
  className,
}: {
  tabs: TabDefinition<K>[];
  active: K;
  onChange: (key: K) => void;
  className?: string;
}) {
  return (
    <div className={cn("border-b border-ink-200", className)} role="tablist">
      <div className="-mb-px flex gap-1 overflow-x-auto">
        {tabs.map((tab) => {
          const unbuilt = Boolean(tab.phase);
          const isActive = tab.key === active;

          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={unbuilt}
              title={unbuilt ? `Arrives in ${tab.phase}` : undefined}
              onClick={() => !unbuilt && onChange(tab.key)}
              className={cn(
                "shrink-0 whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400",
                unbuilt
                  ? "cursor-not-allowed border-transparent text-ink-300"
                  : isActive
                    ? "border-brand-600 text-brand-700"
                    : "border-transparent text-ink-500 hover:border-ink-300 hover:text-ink-800",
              )}
            >
              {tab.label}
              {tab.badge != null && (
                <span
                  className={cn(
                    "ml-1.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums",
                    isActive ? "bg-brand-50 text-brand-700" : "bg-ink-100 text-ink-600",
                  )}
                >
                  {tab.badge}
                </span>
              )}
              {unbuilt && (
                <span className="ml-1.5 rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ring-ink-200">
                  {tab.phase?.replace("Phase ", "P")}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
